import { create } from 'zustand';

export interface AdminUser {
  id: string;
  email: string;
  fullName: string;
  platformRole: string;
}

// localStorage key holding the admin JWT + user so a page refresh keeps the
// session without re-running the OTP flow. Rehydrated by AuthProvider on mount.
export const SESSION_KEY = 'admin_session';

function persistSession(token: string, user: AdminUser) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ token, user }));
  } catch {
    // Storage may be unavailable (private mode, disabled cookies) — the session
    // still works for the current tab; it just won't survive a refresh.
  }
}

interface AuthState {
  token: string | null;
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  setAuth: (token: string, user: AdminUser) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  token: null,
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  setAuth: (token: string, user: AdminUser) => {
    persistSession(token, user);
    set({ token, user, isAuthenticated: true, isLoading: false, error: null });
  },

  logout: () => {
    set({ token: null, user: null, isAuthenticated: false, error: null });
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      // Storage may be unavailable (private mode, disabled cookies) — best-effort clear.
    }
  },

  setLoading: (loading: boolean) => {
    set(loading ? { isLoading: true, error: null } : { isLoading: false });
  },

  setError: (error: string) => {
    set({ error, isLoading: false, isAuthenticated: false, token: null, user: null });
  },
}));
